import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { Formik, Field, FieldArray } from "formik";
import * as Yup from "yup";
import { Col, FormGroup, Form, Row, Button } from "react-bootstrap";
import { formikArrayInput, formikInput } from "../../../utils/utils";
import { Invoice2 } from "src/docuclipper/DocuclipperTypes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TrackedButtonWithTooltip } from "src/views/Docuclipper/TrackedButton";
import {
  handleImportToQbo,
  updateInvoiceField,
} from "src/redux/reducers/InvoiceManager";
import { fetchIntegrations } from "src/redux/reducers/Integration";
import {
  InvoiceFormProps,
  defaultInvoiceLine,
  createBaseValidationSchema,
} from "./invoiceFormUtils";

const InvoiceSage: React.FC<InvoiceFormProps> = ({ invoice, getMarginTop }) => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchIntegrations());
  }, []);

  // Persist a single field change. We mirror the Xero form: dispatch one
  // updateInvoiceField so the whole invoice (including the Sage-specific
  // options below) rides along in a single API call.
  const handleFieldChange = ({
    values,
    field,
    value,
  }: {
    values: Invoice2;
    field: string;
    value: any;
  }) => {
    dispatch(updateInvoiceField({ invoice: values, field, value }));
  };

  const handleLineItemChange = ({
    values,
    lineIndex,
    field,
    value,
  }: {
    values: Invoice2;
    lineIndex: number;
    field: string;
    value: any;
  }) => {
    const updatedValues = { ...values };
    const updatedLines = [...updatedValues.lines];
    updatedLines[lineIndex] = { ...updatedLines[lineIndex], [field]: value };
    updatedValues.lines = updatedLines;
    dispatch(updateInvoiceField({ invoice: updatedValues, field, value }));
  };

  if (!invoice) return null;

  const validationSchema = Yup.object().shape({
    ...createBaseValidationSchema(invoice.docType),
  });

  const inv = invoice as any;
  const initialValues = {
    ...invoice,
    // Sage Business Cloud Accounting export options
    sageLineItemsMapTo: inv.sageLineItemsMapTo || "ledger",
    sageLedgerAccountId: inv.sageLedgerAccountId || "",
    sageTaxRateId: inv.sageTaxRateId || "",
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize={true}
        context={{ docType: invoice.docType }}
        onSubmit={(values: Invoice2) => {
          dispatch(handleImportToQbo(values));
        }}
      >
        {({ values }) => (
          <>
            {/* Sage export options */}
            <Row>
              <Col>
                <FormGroup>
                  <strong>
                    <Form.Label>Map line items to</Form.Label>
                  </strong>
                  <Field
                    as="select"
                    name="sageLineItemsMapTo"
                    className="form-control"
                    onChange={(event: any) =>
                      handleFieldChange({
                        values,
                        field: "sageLineItemsMapTo",
                        value: event.target.value,
                      })
                    }
                  >
                    <option value="ledger">Ledger Accounts</option>
                    <option value="items">Items</option>
                  </Field>
                </FormGroup>
              </Col>
              <Col>
                <FormGroup>
                  <strong>
                    <Form.Label>Ledger account fallback (optional)</Form.Label>
                  </strong>
                  {/* TODO: wire to backend list endpoint once live */}
                  <Field
                    placeholder="Ledger account id"
                    name="sageLedgerAccountId"
                    type="text"
                    component={formikInput}
                    onBlur={(event: any) =>
                      handleFieldChange({
                        values,
                        field: "sageLedgerAccountId",
                        value: event.target.value,
                      })
                    }
                  />
                </FormGroup>
              </Col>
              <Col>
                <FormGroup>
                  <strong>
                    <Form.Label>Tax rate (optional)</Form.Label>
                  </strong>
                  {/* TODO: wire to backend list endpoint once live */}
                  <Field
                    placeholder="Sage tax rate id"
                    name="sageTaxRateId"
                    type="text"
                    component={formikInput}
                    onBlur={(event: any) =>
                      handleFieldChange({
                        values,
                        field: "sageTaxRateId",
                        value: event.target.value,
                      })
                    }
                  />
                </FormGroup>
              </Col>
            </Row>

            {/* ID, Date, Due Date row */}
            <Row>
              <Col>
                <FormGroup>
                  <strong>
                    <Form.Label>Id #</Form.Label>
                  </strong>
                  <Field
                    placeholder=""
                    name="id"
                    type="text"
                    component={formikInput}
                    onBlur={(event: any) =>
                      handleFieldChange({
                        values,
                        field: "id",
                        value: event.target.value,
                      })
                    }
                  />
                </FormGroup>
              </Col>
              <Col>
                <FormGroup>
                  <strong>
                    <Form.Label>Date</Form.Label>
                  </strong>
                  <Field
                    placeholder=""
                    name="date"
                    type="text"
                    component={formikInput}
                    onBlur={(event: any) =>
                      handleFieldChange({
                        values,
                        field: "date",
                        value: event.target.value,
                      })
                    }
                  />
                </FormGroup>
              </Col>
              <Col>
                <FormGroup>
                  <strong>
                    <Form.Label>Due date</Form.Label>
                  </strong>
                  <Field
                    placeholder=""
                    name="dueDate"
                    type="text"
                    component={formikInput}
                    onBlur={(event: any) =>
                      handleFieldChange({
                        values,
                        field: "dueDate",
                        value: event.target.value,
                      })
                    }
                  />
                </FormGroup>
              </Col>
            </Row>

            {/* Subtotal / Tax / Total row */}
            <Row>
              <Col>
                <FormGroup>
                  <strong>
                    <Form.Label>Subtotal</Form.Label>
                  </strong>
                  <Field
                    placeholder=""
                    name="subTotal"
                    type="text"
                    component={formikInput}
                    onBlur={(event: any) =>
                      handleFieldChange({
                        values,
                        field: "subTotal",
                        value: event.target.value,
                      })
                    }
                  />
                </FormGroup>
              </Col>
              <Col>
                <FormGroup>
                  <strong>
                    <Form.Label>Tax</Form.Label>
                  </strong>
                  <Field
                    placeholder=""
                    name="tax"
                    type="text"
                    component={formikInput}
                    onBlur={(event: any) =>
                      handleFieldChange({
                        values,
                        field: "tax",
                        value: event.target.value,
                      })
                    }
                  />
                </FormGroup>
              </Col>
              <Col>
                <FormGroup>
                  <strong>
                    <Form.Label>Total</Form.Label>
                  </strong>
                  <Field
                    placeholder=""
                    name="total"
                    type="text"
                    component={formikInput}
                    onBlur={(event: any) =>
                      handleFieldChange({
                        values,
                        field: "total",
                        value: event.target.value,
                      })
                    }
                  />
                </FormGroup>
              </Col>
            </Row>

            {/* Line Items */}
            <Row className="ml-0">
              <div className="w-100 mb-2">
                <strong>Line Items</strong>
              </div>
              <FieldArray name="lines" key={invoice?.integration}>
                {({ remove, push }) => (
                  <div
                    className="w-100"
                    style={{ maxHeight: "120vh", overflowY: "scroll" }}
                  >
                    {values.lines.length > 0 &&
                      values.lines.map((lineItem: any, index: number) => (
                        <div className="row my-2" key={`line-${index}`}>
                          {/* Description column */}
                          <div className="col-5 pr-0">
                            {index === 0 && (
                              <strong>
                                <label htmlFor={`lines.${index}.item`}>
                                  Description
                                </label>
                              </strong>
                            )}
                            <Field
                              name={`lines.${index}.item`}
                              placeholder=""
                              type="text"
                              component={formikArrayInput}
                              onBlur={(event: any) =>
                                handleLineItemChange({
                                  values,
                                  lineIndex: index,
                                  field: "item",
                                  value: event.target.value,
                                })
                              }
                              style={{
                                marginTop: getMarginTop(
                                  index,
                                  lineItem,
                                  "1rem",
                                  "0"
                                ),
                              }}
                            />
                          </div>

                          {/* Quantity column */}
                          <div className="col-2 pr-0">
                            {index === 0 && (
                              <strong>
                                <label htmlFor={`lines.${index}.quantity`}>
                                  Quantity
                                </label>
                              </strong>
                            )}
                            <Field
                              name={`lines.${index}.quantity`}
                              placeholder=""
                              type="text"
                              component={formikArrayInput}
                              onBlur={(event: any) =>
                                handleLineItemChange({
                                  values,
                                  lineIndex: index,
                                  field: "quantity",
                                  value: event.target.value,
                                })
                              }
                              style={{
                                marginTop: getMarginTop(
                                  index,
                                  lineItem,
                                  "1rem",
                                  "0"
                                ),
                              }}
                            />
                          </div>

                          {/* Unit Price column */}
                          <div className="col-2 pr-0">
                            {index === 0 && (
                              <strong>
                                <label htmlFor={`lines.${index}.unitPrice`}>
                                  Unit Price
                                </label>
                              </strong>
                            )}
                            <Field
                              name={`lines.${index}.unitPrice`}
                              placeholder=""
                              type="text"
                              component={formikArrayInput}
                              onBlur={(event: any) =>
                                handleLineItemChange({
                                  values,
                                  lineIndex: index,
                                  field: "unitPrice",
                                  value: event.target.value,
                                })
                              }
                              style={{
                                marginTop: getMarginTop(
                                  index,
                                  lineItem,
                                  "1rem",
                                  "0"
                                ),
                              }}
                            />
                          </div>

                          {/* Price column */}
                          <div className="col-2 pr-0">
                            {index === 0 && (
                              <strong>
                                <label htmlFor={`lines.${index}.price`}>
                                  Price
                                </label>
                              </strong>
                            )}
                            <Field
                              name={`lines.${index}.price`}
                              placeholder=""
                              type="text"
                              component={formikArrayInput}
                              onBlur={(event: any) =>
                                handleLineItemChange({
                                  values,
                                  lineIndex: index,
                                  field: "price",
                                  value: event.target.value,
                                })
                              }
                              style={{
                                marginTop: getMarginTop(
                                  index,
                                  lineItem,
                                  "1rem",
                                  "0"
                                ),
                              }}
                            />
                          </div>

                          {/* Delete button */}
                          <TrackedButtonWithTooltip
                            style={{
                              display: "flex",
                              alignItems: "center",
                              marginRight: "0.5rem",
                              marginTop: getMarginTop(
                                index,
                                lineItem,
                                "1.5rem",
                                "-1rem"
                              ),
                            }}
                            id={index.toString()}
                            placement="top"
                            variant="link"
                            renderTooltip={() =>
                              values.lines.length === 1 ? (
                                <>Cannot delete last line item</>
                              ) : (
                                <>Delete Line Item</>
                              )
                            }
                            action="removeItem"
                            onClick={() =>
                              values.lines.length > 1 && remove(index)
                            }
                            disabled={values.lines.length === 1}
                          >
                            <FontAwesomeIcon icon="trash" />
                          </TrackedButtonWithTooltip>
                        </div>
                      ))}

                    <Button
                      variant="link"
                      className="secondary"
                      onClick={() => push(defaultInvoiceLine)}
                    >
                      <FontAwesomeIcon icon="plus" />
                      <span className="ml-1">Add Line Item</span>
                    </Button>
                    <div className="p-5"></div>
                  </div>
                )}
              </FieldArray>
            </Row>
          </>
        )}
      </Formik>
    </>
  );
};

export default InvoiceSage;
